import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import styles from './AppNoTenantLanding.module.css'
import { applyMarketingLandingDocumentTheme } from '@/utils/theme'

const IMG = {
  salon: '/landing/salon.jpg',
  phone: '/landing/phone.jpg',
  clinic: '/landing/clinic.jpg',
} as const

const TRUST_ITEMS = [
  'Salões de beleza',
  'Clínicas de estética',
  'Barbearias',
  'Spas & wellness',
  'Estúdios de unhas',
  'Centros de fisioterapia',
] as const

const CONTACT_EMAIL =
  (import.meta.env.VITE_SALES_CONTACT_EMAIL as string | undefined)?.trim() || 'comercial@gobooking.pt'

const SETUP_PRICE =
  (import.meta.env.VITE_SALES_SETUP_PRICE as string | undefined)?.trim() || '637'
const MONTHLY_PRICE =
  (import.meta.env.VITE_SALES_MONTHLY_PRICE as string | undefined)?.trim() || '37,90'
const DAILY_COMPARE =
  (import.meta.env.VITE_SALES_DAILY_COMPARE as string | undefined)?.trim() || '1,30'

const DEMO_SUBJECT = encodeURIComponent('Demonstração GoBooking — quero o meu espaço exclusivo')
const MAIL_DEMO = `mailto:${CONTACT_EMAIL}?subject=${DEMO_SUBJECT}`
const MAIL_CONTACT = `mailto:${CONTACT_EMAIL}`

const EXCLUSIVE_BENEFITS = [
  {
    icon: '🔒',
    title: 'Ambiente só seu',
    text: 'Os seus clientes nunca entram numa “app geral” nem veem outro negócio. É o seu canal — com o seu nome, menu e regras.',
  },
  {
    icon: '✨',
    title: 'Marca em primeiro plano',
    text: 'Logo, mensagens de boas-vindas e fluxo de marcação pensados para reforçar que estão a lidar com você — não com um intermediário.',
  },
  {
    icon: '🛡️',
    title: 'Dados e agenda isolados',
    text: 'Clientes, histórico e disponibilidade ficam no seu tenant. Ninguém partilha a sua lista nem a sua agenda.',
  },
  {
    icon: '📣',
    title: 'Um link que é só seu',
    text: 'Partilhe no Instagram, Google ou cartão: cada clique abre o universo da sua empresa — impossível confundir com a concorrência.',
  },
] as const

const METRICS = [
  { value: '100%', label: 'A sua marca', sub: 'O cliente vê só o seu negócio — nunca uma lista de salões ou clínicas alheias' },
  { value: '1', label: 'Link exclusivo', sub: 'gobooking.pt/e/sua-empresa — reservado e reconhecível' },
  { value: '24/7', label: 'Marcações sem recepção', sub: 'O seu canal trabalha quando a equipa descansa; você mantém o controlo' },
  { value: '0', label: 'Partilha com concorrentes', sub: 'Não é marketplace. É o espaço digital privado do seu negócio' },
] as const

const PAINS = [
  'Marcações perdidas em mensagens que ninguém viu a tempo',
  'Equipa a confirmar o mesmo horário três vezes no WhatsApp',
  'Clientes que desistem porque “é complicado marcar”',
  'Impossível abrir uma segunda unidade sem duplicar o caos',
] as const

const FEATURES = [
  {
    icon: '🔗',
    title: 'URL dedicada à sua empresa',
    text: 'Cada negócio tem o seu endereço permanente. Os clientes guardam o link — e voltam sempre ao mesmo sítio, ao seu.',
  },
  {
    icon: '🎨',
    title: 'Experiência “feita para mim”',
    text: 'Quem marca sente que entrou no site da sua marca: serviços, unidades e profissionais são os seus — não um catálogo genérico.',
  },
  {
    icon: '📅',
    title: 'Agenda que cresce consigo',
    text: 'Várias unidades e profissionais no mesmo espaço exclusivo — sem abrir contas paralelas nem perder a identidade.',
  },
  {
    icon: '📱',
    title: 'Marcação em segundos no telemóvel',
    text: 'Link no browser ou app instalada — o cliente escolhe. Em ambos os casos, só vê a sua marca e marca em poucos toques.',
  },
  {
    icon: '🔔',
    title: 'Menos faltas, mais confiança',
    text: 'Histórico, notificações e visão clara da agenda — o cliente sente organização; você ganha previsibilidade.',
  },
  {
    icon: '👥',
    title: 'Equipa no seu painel privado',
    text: 'Administradores e profissionais com permissões certas — tudo dentro do ambiente da sua empresa, não exposto a outros.',
  },
] as const

const APP_BENEFITS = [
  {
    icon: '📲',
    title: 'Nome e ícone da sua marca',
    text: 'A app aparece nas lojas com o nome do seu negócio — não com a marca de um intermediário.',
  },
  {
    icon: '🛍️',
    title: 'Google Play e App Store',
    text: 'Os clientes instalam como qualquer app profissional. Mais confiança do que “abre este link no browser”.',
  },
  {
    icon: '🔔',
    title: 'Marcações na palma da mão',
    text: 'Agendar, ver histórico, serviços e notificações — tudo dentro do ecossistema exclusivo da sua empresa.',
  },
  {
    icon: '🚀',
    title: 'Publicação tratada por nós',
    text: 'Configuramos listing, builds e envio às lojas. Você recebe os links prontos a partilhar.',
  },
] as const

const PROOF = [
  {
    quote:
      'Os clientes dizem “é o link do nosso salão” — não parece um serviço alugado. O Instagram aponta para nós e só para nós.',
    role: 'Gestora de salão · 2 unidades',
  },
  {
    quote:
      'Ter um espaço só nosso deu-nos credibilidade. Quem marca sente que está na nossa clínica, não numa app qualquer.',
    role: 'Clínica de estética · equipa de 8',
  },
  {
    quote:
      'Um link, uma marca, zero confusão. Parece que tínhamos app própria — sem o custo de desenvolver do zero.',
    role: 'Barbearia · marca em crescimento',
  },
] as const

const FAQ = [
  {
    q: 'É uma app partilhada com outros negócios?',
    a: 'Não. Cada empresa tem o seu ambiente isolado e o seu link (/e/nome-da-empresa). Os seus clientes só veem a sua marca, serviços e equipa.',
  },
  {
    q: 'Preciso de instalar uma app?',
    a: 'Não é obrigatório — o cliente pode marcar pelo link no browser. Mas também recebe uma app com a sua marca na Google Play e na App Store, para quem prefere instalar como qualquer negócio moderno.',
  },
  {
    q: 'Funciona para várias unidades?',
    a: 'Sim. Todas as unidades vivem no mesmo espaço da sua marca — um só link, uma identidade, várias localizações.',
  },
  {
    q: 'Quanto tempo demora a ficar operacional?',
    a: 'Após o onboarding, muitos negócios publicam o link no mesmo dia. Ajudamos na configuração inicial.',
  },
  {
    q: 'Já sou cliente — onde entro?',
    a: 'Use o link que a sua empresa enviou (formato /e/nome-da-empresa). Esta página é para quem quer adquirir o serviço.',
  },
] as const

function Reveal({
  children,
  className = '',
  delay = 0,
}: {
  children: ReactNode
  className?: string
  delay?: number
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduced) {
      setVisible(true)
      return
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setVisible(true)
          io.disconnect()
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -48px 0px' }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      className={`${styles.reveal} ${visible ? styles.revealVisible : ''} ${className}`.trim()}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  )
}

function HeroMockup() {
  return (
    <div className={styles.mockupWrap} aria-hidden>
      <div className={styles.mockupGlow} />
      <div className={styles.phone}>
        <div className={styles.phoneNotch} />
        <div className={styles.phoneScreen}>
          <div className={styles.phoneExclusiveBadge}>Só clientes desta marca</div>
          <div className={styles.phoneHeader}>
            <span className={styles.phoneLogoMark} aria-hidden>
              B
            </span>
            <div className={styles.phoneBrandBlock}>
              <span className={styles.phoneBrand}>Studio Bella</span>
              <span className={styles.phoneUrl}>…/e/studio-bella</span>
            </div>
            <span className={styles.phoneBadge}>● Exclusivo</span>
          </div>
          <div className={styles.phoneBanner}>Bem-vindo ao seu espaço — marque em 30 segundos</div>
          <ul className={styles.phoneMenu}>
            <li>✨ Serviços</li>
            <li>📅 Agendar</li>
            <li>📍 Unidades</li>
            <li>👤 Minha conta</li>
          </ul>
          <div className={styles.phoneSlot}>
            <span>Hoje · 14:30</span>
            <strong>Confirmado</strong>
          </div>
        </div>
      </div>
      <div className={styles.floatingCard}>
        <span className={styles.floatingLabel}>Esta semana</span>
        <strong className={styles.floatingValue}>+38</strong>
        <span className={styles.floatingSub}>novas marcações</span>
      </div>
      <div className={`${styles.floatingCard} ${styles.floatingCardAlt}`}>
        <span className={styles.floatingLabel}>Percepção de marca</span>
        <strong className={styles.floatingValue}>Premium</strong>
        <span className={styles.floatingSub}>“Parece app só nossa”*</span>
      </div>
    </div>
  )
}

function StoreAppVisual() {
  return (
    <div className={styles.storeVisual} aria-hidden>
      <div className={styles.storeGlow} />
      <div className={styles.storeListing}>
        <div className={styles.storeListingHeader}>
          <span className={styles.storeAppIcon}>B</span>
          <div className={styles.storeAppMeta}>
            <strong className={styles.storeAppName}>Studio Bella</strong>
            <span className={styles.storeAppTag}>GoBooking · Só clientes desta marca</span>
            <div className={styles.storeStars}>
              ★★★★★ <span>5,0</span>
            </div>
          </div>
        </div>
        <div className={styles.storeScreenshots}>
          <span />
          <span />
          <span />
        </div>
        <p className={styles.storeDesc}>
          Marque serviços, escolha profissional e unidade — tudo com a identidade do seu negócio.
        </p>
      </div>
      <div className={styles.storeBadges}>
        <span className={styles.storeBadge}>
          <span className={styles.storeBadgeIcon} aria-hidden>
            ▶
          </span>
          <span>
            <small>Disponível na</small>
            <strong>Google Play</strong>
          </span>
        </span>
        <span className={styles.storeBadge}>
          <span className={styles.storeBadgeIcon} aria-hidden>
            🍎
          </span>
          <span>
            <small>Transferir na</small>
            <strong>App Store</strong>
          </span>
        </span>
      </div>
      <div className={styles.storeFloating}>
        <span className={styles.storeFloatingLabel}>Perceção do cliente</span>
        <strong>“Têm app própria”</strong>
      </div>
    </div>
  )
}

function GrowthChart() {
  return (
    <svg className={styles.chart} viewBox="0 0 400 200" role="img" aria-label="Gráfico ilustrativo de crescimento de marcações">
      <defs>
        <linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0ea5e9" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="chartStroke" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#0284c7" />
          <stop offset="100%" stopColor="#38bdf8" />
        </linearGradient>
      </defs>
      <path
        d="M0 160 L60 140 L120 130 L180 110 L240 95 L300 70 L360 45 L400 30 L400 200 L0 200 Z"
        fill="url(#chartFill)"
      />
      <path
        className={styles.chartLine}
        d="M0 160 L60 140 L120 130 L180 110 L240 95 L300 70 L360 45 L400 30"
        fill="none"
        stroke="url(#chartStroke)"
        strokeWidth="3"
        strokeLinecap="round"
      />
      {[
        [0, 160],
        [60, 140],
        [120, 130],
        [180, 110],
        [240, 95],
        [300, 70],
        [360, 45],
        [400, 30],
      ].map(([cx, cy], i) => (
        <circle key={i} cx={cx} cy={cy} r="5" fill="#fff" stroke="#0284c7" strokeWidth="2" />
      ))}
    </svg>
  )
}

export function AppNoTenantLanding() {
  const [scrolled, setScrolled] = useState(false)
  const [showStickyCta, setShowStickyCta] = useState(false)

  useEffect(() => {
    applyMarketingLandingDocumentTheme()
    document.title = 'GoBooking — Espaço exclusivo de marcações | Sol Online'
    const desc =
      'Link exclusivo, app com a sua marca e agenda isolada. GoBooking pela Sol Online — o canal digital só do seu negócio.'
    let meta = document.querySelector('meta[name="description"]')
    if (!meta) {
      meta = document.createElement('meta')
      meta.setAttribute('name', 'description')
      document.head.appendChild(meta)
    }
    meta.setAttribute('content', desc)
  }, [])

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY
      setScrolled(y > 24)
      setShowStickyCta(y > 520)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <div className={styles.root}>
      <div className={styles.bgBase} aria-hidden />
      <div className={styles.bgMesh} aria-hidden />
      <div className={styles.bgGrid} aria-hidden />
      <div className={styles.bgOrbs} aria-hidden>
        <span className={styles.orbA} />
        <span className={styles.orbB} />
        <span className={styles.orbC} />
      </div>

      <header className={`${styles.nav} ${scrolled ? styles.navScrolled : ''}`}>
        <a href="#" className={styles.navBrand} aria-label="GoBooking por Sol Online">
          <img
            className={styles.navLogoImg}
            src="/brand/logo-solonline.svg"
            alt="GoBooking por Sol Online"
            width={280}
            height={52}
            decoding="async"
          />
        </a>
        <nav className={styles.navLinks} aria-label="Secções">
          <a href="#exclusivo">Exclusividade</a>
          <a href="#beneficios">Benefícios</a>
          <a href="#app">App</a>
          <a href="#precos">Preços</a>
          <a href="#faq">FAQ</a>
        </nav>
        <a className={styles.navCta} href={MAIL_DEMO}>
          Agendar demonstração
        </a>
      </header>

      <section className={styles.hero}>
        <div className={`${styles.heroCopy} ${styles.heroPanel}`}>
          <p className={styles.eyebrow}>
            <span className={styles.eyebrowPulse} aria-hidden />
            A sua marca · O seu link · Só os seus clientes
          </p>
          <h1 className={styles.heroTitle}>
            Não é uma app qualquer.
            <span className={styles.heroTitleGradient}> É o espaço digital exclusivo do seu negócio.</span>
          </h1>
          <p className={styles.heroLead}>
            Enquanto outros perdem clientes em mensagens genéricas, você oferece um{' '}
            <strong>canal próprio</strong> onde só existe a sua empresa — com o seu nome, serviços e agenda. Quem
            marca sente que entrou <strong>no seu mundo</strong>, não num marketplace partilhado.
          </p>
          <div className={styles.heroCtas}>
            <a className={styles.btnPrimary} href={MAIL_DEMO}>
              Quero o meu espaço exclusivo
            </a>
            <a className={styles.btnGhost} href="#exclusivo">
              Porque é único →
            </a>
          </div>
          <p className={styles.heroNote}>
            Link dedicado · App nas lojas · Ambiente isolado · Sem compromisso na demonstração · Portugal
          </p>
        </div>
        <HeroMockup />
      </section>

      <div className={styles.trustStrip} aria-hidden>
        <div className={styles.trustTrack}>
          {[...TRUST_ITEMS, ...TRUST_ITEMS].map((item, i) => (
            <span key={`${item}-${i}`} className={styles.trustPill}>
              {item}
            </span>
          ))}
        </div>
      </div>

      <Reveal delay={40}>
        <section className={styles.photoStrip} aria-label="Ambientes reais">
          <figure className={styles.photoCard}>
            <img
              src={IMG.salon}
              alt="Recepção moderna de salão preparada para marcações digitais"
              width={1280}
              height={720}
              loading="lazy"
              decoding="async"
            />
            <figcaption>Salões que centralizam a agenda num link só seu</figcaption>
          </figure>
          <figure className={styles.photoCard}>
            <img
              src={IMG.phone}
              alt="Cliente a marcar horário no telemóvel"
              width={1024}
              height={768}
              loading="lazy"
              decoding="async"
            />
            <figcaption>O cliente marca em segundos — no browser ou na app</figcaption>
          </figure>
          <figure className={styles.photoCard}>
            <img
              src={IMG.clinic}
              alt="Sala de espera de clínica com ambiente premium"
              width={1280}
              height={720}
              loading="lazy"
              decoding="async"
            />
            <figcaption>Clínicas e estúdios com imagem premium de ponta a ponta</figcaption>
          </figure>
        </section>
      </Reveal>

      <Reveal>
      <section className={styles.metrics} aria-label="Impacto">
        <div className={styles.metricsShell}>
          <div className={styles.metricsHead}>
            <p className={styles.sectionTag}>Impacto imediato</p>
            <h2 className={styles.metricsTitle}>Resultados que o seu cliente sente</h2>
          </div>
          <div className={styles.metricsInner}>
          {METRICS.map((m, i) => (
            <article key={m.label} className={styles.metricCard}>
              <span className={styles.metricIndex}>{String(i + 1).padStart(2, '0')}</span>
              <span className={styles.metricValue}>{m.value}</span>
              <span className={styles.metricLabel}>{m.label}</span>
              <p className={styles.metricSub}>{m.sub}</p>
            </article>
          ))}
        </div>
        <p className={styles.metricsDisclaimer}>
          * Métricas de percepção e operação ilustrativas. O impacto real depende de como divulga o seu link exclusivo.
        </p>
        </div>
      </section>
      </Reveal>

      <Reveal delay={80}>
      <section id="exclusivo" className={styles.exclusiveSection}>
        <div className={styles.exclusivePanel}>
        <div className={styles.exclusiveGrid}>
          <div className={styles.exclusiveIntro}>
            <p className={styles.sectionTag}>Exclusividade real</p>
            <h2 className={styles.exclusiveHeading}>
              O cliente não “usa uma app genérica”.
              <br />
              Entra no <em className={styles.emAccent}>seu</em> negócio.
            </h2>
            <p className={styles.exclusiveLead}>
              Genericidade afasta. Exclusividade vende. Quando o link tem o seu nome, a marcação deixa de ser uma
              tarefa — passa a ser parte da experiência premium que já oferece no salão, na clínica ou no estúdio.
            </p>
            <div className={styles.urlShowcase}>
              <span className={styles.urlShowcaseLabel}>O seu endereço permanente</span>
              <code className={styles.urlShowcaseCode}>
                gobooking.pt/e/<strong>sua-empresa</strong>
              </code>
              <p className={styles.urlShowcaseHint}>
                Reservado para si. Os clientes guardam, partilham e voltam sempre ao mesmo sítio — só seu.
              </p>
            </div>
            <div className={styles.exclusivePhoto}>
              <img
                src={IMG.phone}
                alt="Marcação no telemóvel com a marca do negócio"
                width={1024}
                height={768}
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
          <div className={styles.exclusiveCards}>
            {EXCLUSIVE_BENEFITS.map((b) => (
              <article key={b.title} className={styles.exclusiveCard}>
                <span className={styles.exclusiveIcon} aria-hidden>
                  {b.icon}
                </span>
                <h3 className={styles.exclusiveTitle}>{b.title}</h3>
                <p className={styles.exclusiveText}>{b.text}</p>
              </article>
            ))}
          </div>
        </div>
        <div className={styles.exclusiveStrip}>
          <span className={styles.stripIcon} aria-hidden>💬</span>
          <p>
            <strong>Imagine o cliente a dizer:</strong> “Marca no link do nosso salão” — não “procura uma app
            qualquer”. Essa diferença é posicionamento. É desejo. É fidelização antes da primeira visita.
          </p>
        </div>
        </div>
      </section>
      </Reveal>

      <Reveal delay={100}>
      <section className={styles.painSection}>
        <div className={styles.painGrid}>
          <div className={styles.painCopy}>
            <p className={styles.sectionTag}>Antes vs. depois</p>
            <h2 className={styles.painHeading}>Genericidade custa clientes</h2>
            <p className={styles.painLead}>
              Quando marcação é confusa ou “parece de terceiros”, o cliente desconfia — e vai para quem já tem um
              canal claro e só deles.
            </p>
            <ul className={styles.painList}>
              {PAINS.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </div>
          <div className={styles.painVisual}>
            <span className={styles.painVs} aria-hidden>
              VS
            </span>
            <div className={styles.painCardBad}>
              <span className={styles.painCardLabel}>Canal genérico</span>
              <ul>
                <li>WhatsApp misturado com vida pessoal</li>
                <li>Cliente não sabe onde marcar</li>
                <li>Parece negócio pequeno ou desorganizado</li>
                <li>Concorrente com link próprio ganha</li>
              </ul>
            </div>
            <div className={styles.painCardGood}>
              <span className={styles.painCardLabel}>O seu espaço exclusivo</span>
              <ul>
                <li>Um link = só a sua marca</li>
                <li>Experiência premium e memorável</li>
                <li>Cliente orgulhoso de partilhar</li>
                <li>Você controla agenda e imagem</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      </Reveal>

      <Reveal delay={120}>
      <section id="beneficios" className={styles.features}>
        <div className={styles.sectionHead}>
          <p className={styles.sectionTag}>Funcionalidades</p>
          <h2 className={styles.featuresTitle}>Benefícios que só fazem sentido no seu espaço</h2>
          <p className={styles.sectionLead}>
            Cada funcionalidade reforça a mesma mensagem: isto é <strong>seu</strong>. Organização por dentro,
            exclusividade por fora — para escalar sem parecer “mais um” no mercado.
          </p>
        </div>
        <div className={styles.featureGrid}>
          {FEATURES.map((f, i) => (
            <article key={f.title} className={styles.featureCard} style={{ animationDelay: `${i * 0.05}s` }}>
              <span className={styles.featureIcon} aria-hidden>
                {f.icon}
              </span>
              <h3 className={styles.featureTitle}>{f.title}</h3>
              <p className={styles.featureText}>{f.text}</p>
            </article>
          ))}
        </div>
      </section>
      </Reveal>

      <Reveal delay={100}>
      <section id="app" className={styles.appSection}>
        <div className={styles.appInner}>
          <div className={styles.appGrid}>
            <div className={styles.appCopy}>
              <div className={styles.appHead}>
                <p className={styles.sectionTag}>Aplicação móvel</p>
                <h2 className={styles.appTitle}>
                  A sua marca na Google Play e na App Store
                </h2>
                <p className={styles.appLead}>
                  Para além do link exclusivo, o seu negócio recebe uma <strong>app com o seu nome e ícone</strong>{' '}
                  nas lojas oficiais. Os clientes instalam como qualquer salão ou clínica premium — sem confundir com
                  apps genéricas ou concorrentes.
                </p>
              </div>
              <ul className={styles.appBenefits}>
                {APP_BENEFITS.map((b) => (
                  <li key={b.title}>
                    <span className={styles.appBenefitIcon} aria-hidden>
                      {b.icon}
                    </span>
                    <div>
                      <strong>{b.title}</strong>
                      <p>{b.text}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <a className={styles.btnPrimary} href={MAIL_DEMO}>
                Quero a app do meu negócio
              </a>
            </div>
            <StoreAppVisual />
          </div>
        </div>
      </section>
      </Reveal>

      <Reveal delay={100}>
      <section id="escalar" className={styles.scaleSection}>
        <div className={styles.scaleInner}>
        <div className={styles.scaleGrid}>
          <div className={`${styles.scaleCopy} ${styles.scaleCopyCard}`}>
            <p className={styles.sectionTag}>Crescimento</p>
            <h2 className={styles.scaleHeading}>
              Exclusividade hoje.
              <br />
              <em>Escala amanhã</em> — sem perder a sua identidade.
            </h2>
            <p className={styles.scaleLead}>
              O negócio que cresce sem canal próprio dilui a marca. O que tem um espaço exclusivo transforma cada
              partilha do link em prova social: “são organizados”, “têm sistema próprio”, “vale a pena marcar
              connosco”.
            </p>
            <ul className={styles.scaleList}>
              <li>
                <strong>Desejo antes da visita</strong> — o cliente quer pertencer ao seu ecossistema digital
              </li>
              <li>
                <strong>Receita mais previsível</strong> — agenda cheia com menos esforço da recepção
              </li>
              <li>
                <strong>Segunda unidade, mesma marca</strong> — um link, várias localizações, zero confusão
              </li>
            </ul>
            <a className={styles.btnPrimary} href={MAIL_DEMO}>
              Quero reservar o meu espaço
            </a>
          </div>
          <div className={styles.scaleChartWrap}>
            <GrowthChart />
            <p className={styles.chartCaption}>Evolução ilustrativa de marcações após centralizar o link da marca</p>
          </div>
        </div>
        </div>
      </section>
      </Reveal>

      <Reveal>
      <section className={styles.proof}>
        <div className={styles.proofHead}>
          <p className={styles.sectionTag}>Depoimentos</p>
          <h2 className={styles.proofTitle}>Quem tem espaço próprio, sente a diferença</h2>
          <p className={styles.proofLead}>
            Negócios que passaram de “manda DM” para um link exclusivo da marca — e notaram o impacto na percepção.
          </p>
        </div>
        <div className={styles.proofGrid}>
          {PROOF.map((p) => (
            <blockquote key={p.role} className={styles.proofCard}>
              <span className={styles.proofMark} aria-hidden>“</span>
              <div className={styles.proofStars} aria-hidden>
                ★★★★★
              </div>
              <p className={styles.proofQuote}>{p.quote}</p>
              <footer className={styles.proofRole}>{p.role}</footer>
            </blockquote>
          ))}
        </div>
      </section>
      </Reveal>

      <Reveal delay={80}>
      <section className={styles.stepsSection}>
        <div className={styles.stepsInner}>
        <div className={styles.stepsHead}>
          <p className={styles.sectionTag}>Como começar</p>
          <h2 className={styles.stepsTitle}>Três passos para estar no ar</h2>
        </div>
        <ol className={styles.steps}>
          <li>
            <div className={styles.stepTrack}>
              <span className={styles.stepNum}>1</span>
            </div>
            <div className={styles.stepBody}>
              <strong>Demonstração e plano</strong>
              <p>Alinhamos o seu modelo (unidades, profissionais, serviços) e o plano adequado.</p>
            </div>
          </li>
          <li>
            <div className={styles.stepTrack}>
              <span className={styles.stepNum}>2</span>
            </div>
            <div className={styles.stepBody}>
              <strong>O seu link exclusivo</strong>
              <p>
                Recebe <code className={styles.code}>gobooking.pt/e/sua-empresa</code> e a sua app nas
                lojas — prontos a partilhar.
              </p>
            </div>
          </li>
          <li>
            <div className={styles.stepTrack}>
              <span className={styles.stepNum}>3</span>
            </div>
            <div className={styles.stepBody}>
              <strong>Equipa formada, clientes a marcar</strong>
              <p>Configuramos consigo; os clientes começam a preencher a agenda sem fricção.</p>
            </div>
          </li>
        </ol>
        </div>
      </section>
      </Reveal>

      <Reveal>
      <section id="faq" className={styles.faq}>
        <div className={styles.faqHead}>
          <p className={styles.sectionTag}>Dúvidas</p>
          <h2 className={styles.faqTitle}>Perguntas frequentes</h2>
        </div>
        <div className={styles.faqList}>
          {FAQ.map((item) => (
            <details key={item.q} className={styles.faqItem}>
              <summary>{item.q}</summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
      </section>
      </Reveal>

      <Reveal>
        <section id="precos" className={styles.pricingSection} aria-labelledby="pricing-heading">
          <div className={styles.pricingInner}>
            <div className={styles.pricingHead}>
              <p className={styles.sectionTag}>Investimento</p>
              <h2 id="pricing-heading" className={styles.pricingTitle}>
                Tecnologia inacessível? <span className={styles.pricingTitleAccent}>Já não.</span>
              </h2>
              <p className={styles.pricingSubtitle}>
                Um espaço exclusivo GoBooking para a sua marca — sem desenvolvimento à medida nem custos
                escondidos.
              </p>
            </div>

            <div className={styles.pricingGrid}>
              <div className={styles.pricingCol}>
                <span className={styles.pricingLabel}>Setup único e criação</span>
                <p className={styles.pricingAmount}>
                  {SETUP_PRICE}
                  <span className={styles.pricingCurrency}>€</span>
                </p>
                <p className={styles.pricingColHint}>Configuração inicial, link da marca e onboarding assistido</p>
              </div>
              <div className={styles.pricingDivider} aria-hidden />
              <div className={styles.pricingCol}>
                <span className={styles.pricingLabel}>Servidor &amp; manutenção</span>
                <p className={styles.pricingAmount}>
                  {MONTHLY_PRICE}
                  <span className={styles.pricingCurrency}>€</span>
                  <span className={styles.pricingPeriod}>/mês</span>
                </p>
                <p className={styles.pricingColHint}>Hospedagem, atualizações e suporte da plataforma</p>
              </div>
            </div>

            <div className={styles.pricingValueCard}>
              <span className={styles.pricingValueIcon} aria-hidden>
                ☕
              </span>
              <p className={styles.pricingValueTitle}>Por menos de {DAILY_COMPARE}€ por dia</p>
              <p className={styles.pricingValueText}>
                Investe mais num café diário do que para ter o{' '}
                <strong>controlo digital absoluto do seu negócio</strong> — com um link exclusivo na mão dos seus
                clientes, disponível 24 horas por dia.
              </p>
            </div>

            <ul className={styles.pricingGuarantees}>
              <li>Demonstração sem compromisso</li>
              <li>Onboarding assistido pela Sol Online</li>
              <li>Ambiente isolado — os seus dados não se misturam</li>
            </ul>

            <a className={`${styles.btnPrimaryLg} ${styles.pricingCta}`} href={MAIL_DEMO}>
              Quero o meu espaço exclusivo hoje
            </a>
            <p className={styles.pricingNote}>
              Valores indicativos · IVA não incluído, salvo indicação em contrário ·{' '}
              <a href={MAIL_CONTACT}>Fale connosco</a> para um plano à medida
            </p>
          </div>
        </section>
      </Reveal>

      <Reveal delay={60}>
      <section className={styles.finalCta}>
        <div className={styles.finalCtaInner}>
          <p className={styles.sectionTag}>Próximo passo</p>
          <h2 className={styles.finalTitle}>
            O seu link exclusivo ainda está disponível.
            <br />
            Quer que os seus clientes sintam que é só seu?
          </h2>
          <p className={styles.finalLead}>
            Em 30 minutos mostramos como fica o ambiente da sua marca — URL, painel e experiência do cliente. Sem
            compromisso: só a sensação de ter algo que ninguém mais pode copiar com um clique genérico.
          </p>
          <div className={styles.finalButtons}>
            <a className={styles.btnPrimaryLg} href={MAIL_DEMO}>
              Quero ver o meu espaço exclusivo
            </a>
            <a className={styles.btnOutlineLg} href={MAIL_CONTACT}>
              {CONTACT_EMAIL}
            </a>
          </div>
          <p className={styles.finalUrgency}>
            Vagas limitadas para onboarding assistido este trimestre — garanta a sua demonstração.
          </p>
        </div>
      </section>
      </Reveal>

      {showStickyCta ? (
        <div className={styles.stickyCta} role="region" aria-label="Pedido de demonstração">
          <div className={styles.stickyCtaInner}>
            <p className={styles.stickyCtaText}>
              <strong>Reserve o seu link</strong>
              <span>Demonstração em 30 min · sem compromisso</span>
            </p>
            <a className={styles.stickyCtaBtn} href={MAIL_DEMO}>
              Agendar demonstração
            </a>
          </div>
        </div>
      ) : null}

      <footer className={styles.footer}>
        <div className={styles.footerCard}>
        <img
          className={styles.footerLogo}
          src="/brand/logo-horizontal.svg"
          alt="GoBooking"
          width={160}
          height={32}
          loading="lazy"
          decoding="async"
        />
        <p className={styles.footerLead}>
          <strong>Já é cliente?</strong> Entre pelo link que a empresa lhe enviou — não use esta página para login.
        </p>
        <p className={styles.footerHint}>
          Exemplo: <code className={styles.code}>https://gobooking.pt/e/nome-da-empresa</code>
        </p>
        <p className={styles.footerCopy}>© {new Date().getFullYear()} GoBooking · Sol Online</p>
        <p className={styles.footerLegal}>
          <Link to="/privacidade">Política de Privacidade</Link>
          {' · '}
          <Link to="/termos">Termos de Utilização</Link>
          {' · '}
          <Link to="/eliminar-conta">Eliminar conta</Link>
        </p>
        </div>
      </footer>
      </div>
  )
}
